import * as sentry from '@sentry/react';
import {call, put, select, takeLatest} from 'redux-saga/effects';

import {config} from 'config/env';
import {getAllStoreAreasV2, getStoreArea} from 'services/location/location-service';
import {getMakroMembershipCardsV2, getStoreAreasV1, getUserDetails} from 'services/user/user-service';
import {
  fetchMakroMembershipCardAction,
  setMembershipCardsAction,
  setDefaultMembershipCardAction,
  getStoreAreasAction,
  setStoreAreasAction,
  setIsAppLoadingAction,
  setCurrentStoreWithLocationLocalesAction,
  setCurrentStoreAction,
  setIsStoreInactiveAction
} from 'store/actions/app-action';
import {fetchUserDetailsAction, setUserDetailsAction} from 'store/actions/auth-action';
import {setBasketDetailsAction} from 'store/actions/basket-action';
import {CurrentStoreType, getStoreAreaFilter} from 'store/types/app';
import {StoreArea, UserDetail} from 'types';
import {SubDistrict} from 'types/location';
import {MembershipCard} from 'types/membership';

function* fetchMakroMembershipCardSaga(action: {payload: string}): Generator {
  try {
    yield put(setIsAppLoadingAction(true));
    const accessToken = action.payload;
    const response = (yield call(() => getMakroMembershipCardsV2(accessToken as string))) as MembershipCard[];
    const defaultMembershipCard = response?.find((card) => card.isDefault);
    yield put(setMembershipCardsAction(response));
    yield put(setDefaultMembershipCardAction(defaultMembershipCard));
  } catch (error) {
    sentry.captureException(error);
  } finally {
    yield put(setIsAppLoadingAction(false));
  }
}

function* fetchStoreAreasSaga(): Generator {
  try {
    const storeAreas: any = (yield call(() =>
      config.IS_LOCATION_SVC_ENABLED ? getAllStoreAreasV2() : getStoreAreasV1()
    )) as StoreArea[];
    if (storeAreas) {
      yield put(setStoreAreasAction(storeAreas));
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* setCurrentStoreWithLocationLocales(action: {payload: CurrentStoreType}): Generator {
  try {
    const payload: getStoreAreaFilter = {
      subDistrictName: action.payload.subdistrict,
      zipCode: action.payload.zipcode,
      exactMatch: true
    };
    const response: SubDistrict = (yield call(() => getStoreArea(payload))) as SubDistrict;
    if (response && action.payload) {
      yield put(
        setCurrentStoreAction({...action.payload, subdistrictEn: response.nameEn, subdistrictTh: response.nameTh})
      );
      yield put(setIsStoreInactiveAction(false));
    } else {
      yield put(setIsStoreInactiveAction(true));
      yield put(setCurrentStoreAction());
      yield put(setBasketDetailsAction({basketItems: [], unavailableItems: []}));
    }
  } catch (e: any) {
    sentry.captureException(e);
  }
}

function* fetchUserDetailsSaga(): Generator {
  const accessToken = yield select((state) => state.auth?.idToken);
  try {
    const userDetails = (yield call(() => getUserDetails())) as UserDetail;

    if (userDetails) {
      yield put(setUserDetailsAction(userDetails));
    }
  } catch (e: any) {
    const extra = {idToken: accessToken};
    sentry.captureException(e, {extra});
  }
}

export default function* () {
  yield takeLatest(fetchMakroMembershipCardAction, fetchMakroMembershipCardSaga);
  yield takeLatest(getStoreAreasAction, fetchStoreAreasSaga);
  yield takeLatest(fetchUserDetailsAction, fetchUserDetailsSaga);
  yield takeLatest(setCurrentStoreWithLocationLocalesAction, setCurrentStoreWithLocationLocales);
}
